import { chipBudget } from '@/challenges/chips';
import { allowsChallengePlacement } from '@/challenges/editing';
import type Context from '@/context';
import type { IChipModel, ITileManagerModel } from '@/interfaces/models';
import { Point } from '@/models/Point';
import { Rotation, rotatePointAroundOrigin } from '@/models/data/Rotation';
import { isValidTilePosition } from '@/models/TileManagerModel';
import { CircuitType } from '@/schemas/common';
import { isStructureContiguous } from '@/utils/structureContiguity';

export function getChipSelectability(
  chip: IChipModel,
  editingChip: IChipModel,
  context: Context,
): { allowed: boolean; reason?: string } {
  if (chip.templateType)
    return { allowed: false, reason: 'Select this template to stamp its contents.' };
  if (context.challenge && !chipBudget(context, chip.id)?.allowed)
    return { allowed: false, reason: 'This chip is not available in this challenge.' };
  const owner = context.app?.getSourceChip?.(editingChip) ?? editingChip;
  if (context.userPreferences?.recursiveChipsEnabled !== true) {
    if (chip.id === owner.id)
      return { allowed: false, reason: 'Cannot place the chip inside itself' };
    if (chip.recursiveChildChips.some((child) => child.id === owner.id))
      return { allowed: false, reason: 'Circular dependency detected' };
  }
  if (!isStructureContiguous(chip.structureCircuit.circuitModel.tileManagerModel))
    return { allowed: false, reason: 'Structure tiles must be contiguous' };
  return { allowed: true };
}

/** The authored cells, rather than the bounding rectangle, define collisions.
 * Invalid cells remain in the visual plan; only the action requires allowed. */
export function getChipPlacementResult({
  context,
  chip,
  anchor,
  rotation = Rotation.CW_0,
  tileManager,
  circuitType,
  editingChip = context.editor?.currentChip,
}: {
  context: Context;
  chip: IChipModel;
  anchor: Point;
  rotation?: Rotation;
  tileManager: ITileManagerModel;
  circuitType: CircuitType;
  editingChip?: IChipModel;
}) {
  const origin = Point.fromData(chip.structureCircuit.originLocation.data);
  const offset = anchor.sub(origin);
  const tiles = chip.structureCircuit.circuitModel.tileManagerModel.tiles.map(
    (source) => {
      const location = rotatePointAroundOrigin(
        Point.fromData(source.location),
        origin,
        rotation,
      ).add(offset);
      const inBounds =
        isValidTilePosition(location.x, location.y) &&
        allowsChallengePlacement(context, tileManager, location);
      return {
        source,
        location,
        inBounds,
        overlapping: inBounds && tileManager.has(location),
      };
    },
  );
  // Low-level graph/import callers may have no identified editor owner. Their
  // geometric checks still apply; production editors always have a chip id.
  const selection = editingChip?.id
    ? getChipSelectability(chip, editingChip, context)
    : { allowed: true };
  const overlaps = tiles.filter((tile) => tile.overlapping);
  const reasons: string[] = [];
  if (circuitType !== CircuitType.ChipLogic)
    reasons.push('Chips can only be placed in a logic circuit');
  if (!selection.allowed && selection.reason) reasons.push(selection.reason);
  if (!tiles.length) reasons.push('Chip structure is empty');
  if (tiles.some((tile) => !tile.inBounds))
    reasons.push('Chip extends beyond the circuit boundary');
  if (overlaps.length)
    reasons.push(
      `${overlaps.length} chip ${overlaps.length === 1 ? 'cell overlaps' : 'cells overlap'} existing tiles`,
    );
  return {
    allowed: reasons.length === 0,
    reason: reasons.join('. ') || null,
    tiles,
    overlaps,
  };
}
